/* eslint-disable no-nested-ternary */
/* eslint-disable camelcase */
import { useState, useEffect, useCallback, useId } from 'react';
import PropTypes from 'prop-types';
import {
    Box, Checkbox, ClickAwayListener, Divider, FormControlLabel,
    MenuItem, Radio, RadioGroup, Select, TextField, Tooltip, Typography,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { styled } from '@mui/material/styles';
import { CONSENT_FIELD_TYPES } from 'src/pages/superAdmin/ConsentV2/consentConstants';
import { consentAutofillProps } from 'src/utils/consentAutofill';
import ConsentCheckboxGroup from './ConsentCheckboxGroup';
import ConsentSignaturePad from './ConsentSignaturePad';

const FIELD_ERROR_HIGHLIGHT_CLASS = 'consent-field-error-highlight';

// Inject keyframes once
if (typeof document !== 'undefined' && !document.getElementById('consent-error-keyframes')) {
    const style = document.createElement('style');
    style.id = 'consent-error-keyframes';
    style.textContent = `
        @keyframes consentFieldShake {
            0%, 100% { transform: translateX(0); }
            15%, 45%, 75% { transform: translateX(-4px); }
            30%, 60%, 90% { transform: translateX(4px); }
        }
        @keyframes consentFieldPulse {
            0% { box-shadow: 0 0 0 0 rgba(255, 72, 66, 0.4); }
            70% { box-shadow: 0 0 0 8px rgba(255, 72, 66, 0); }
            100% { box-shadow: 0 0 0 0 rgba(255, 72, 66, 0); }
        }
        .${FIELD_ERROR_HIGHLIGHT_CLASS} {
            animation: consentFieldShake 0.5s ease-in-out, consentFieldPulse 1s ease-in-out;
            border-radius: 8px;
            outline: 2px solid #FF4842;
            outline-offset: 4px;
        }
    `;
    document.head.appendChild(style);
}

export { FIELD_ERROR_HIGHLIGHT_CLASS };

const HtmlContentWrapper = styled(Box)(({ theme }) => ({
    wordBreak: 'break-word',
    overflowWrap: 'break-word',

    // ── Headings ──
    '& h1, & h2, & h3, & h4, & h5, & h6': {
        marginTop: theme.spacing(2),
        marginBottom: theme.spacing(1),
        fontFamily: 'inherit',
    },
    '& h1': { fontSize: '1.75em' },
    '& h2': { fontSize: '1.35em' },
    '& h3': { fontSize: '1.1em' },

    // ── Paragraphs — tighter spacing to match real forms ──
    '& p': {
        marginTop: 0,
        marginBottom: theme.spacing(0.75),
        lineHeight: 1.7,
    },
    '& p + p': {
        marginTop: 0,
    },
    '& p:empty, & p > br:only-child': {
        marginBottom: theme.spacing(0.25),
    },

    // ── Lists ──
    '& ul, & ol': {
        paddingLeft: theme.spacing(3),
        marginBottom: theme.spacing(1),
        marginTop: theme.spacing(0.5),
    },
    '& li': {
        marginBottom: theme.spacing(0.25),
        lineHeight: 1.7,
    },

    // ── Quill indentation classes ──
    '& .ql-indent-1': { paddingLeft: '3em' },
    '& .ql-indent-2': { paddingLeft: '6em' },
    '& .ql-indent-3': { paddingLeft: '9em' },
    '& .ql-indent-4': { paddingLeft: '12em' },
    '& .ql-indent-5': { paddingLeft: '15em' },
    '& .ql-indent-6': { paddingLeft: '18em' },
    '& .ql-indent-7': { paddingLeft: '21em' },
    '& .ql-indent-8': { paddingLeft: '24em' },

    // ── Quill alignment classes ──
    '& .ql-align-center': { textAlign: 'center' },
    '& .ql-align-right': { textAlign: 'right' },
    '& .ql-align-justify': { textAlign: 'justify' },

    // ── Quill size classes ──
    '& .ql-size-small': { fontSize: '0.75em' },
    '& .ql-size-large': { fontSize: '1.5em' },
    '& .ql-size-huge': { fontSize: '2.5em' },

    // ── Blockquote ──
    '& blockquote': {
        borderLeft: `4px solid ${theme.palette.divider}`,
        paddingLeft: theme.spacing(2),
        margin: theme.spacing(1, 0),
        color: theme.palette.text.secondary,
        fontStyle: 'italic',
    },

    // ── Inline ──
    '& a': {
        color: theme.palette.primary.main,
        textDecoration: 'underline',
    },
    '& strong': {
        fontWeight: 600,
    },

    // ── Force font inheritance on Quill-generated spans ──
    '& span': {
        fontFamily: 'inherit',
    },

    // ── Table support ──
    '& table': {
        borderCollapse: 'collapse',
        width: '100%',
        marginBottom: theme.spacing(1),
    },
    '& td, & th': {
        border: `1px solid ${theme.palette.divider}`,
        padding: theme.spacing(0.75, 1),
    },
}));

const HEADING_VARIANT_MAP = {
    h1: 'h3',
    h2: 'h4',
    h3: 'h5',
    h4: 'h6',
    h5: 'subtitle1',
    h6: 'subtitle2',
};

// Convert a date value (string or Date) to a Date object for DatePicker
function parseDateValue(val) {
    if (!val) return null;
    if (val instanceof Date) return val;
    const d = new Date(val);
    if (!Number.isNaN(d.getTime())) return d;
    return null;
}

// Normalize Quill HTML: collapse empty paragraphs, remove trailing blanks
function normalizeQuillHtml(html) {
    if (!html) return '';
    return html
        .replace(/(<p><br><\/p>\s*){2,}/gi, '<p><br></p>')
        .replace(/<p><br><\/p>\s*$/i, '');
}

// Format Date object to yyyy-mm-dd string for storage
function formatDateForStorage(date) {
    if (!date || Number.isNaN(date.getTime())) return '';
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
}

// ── Toggle Ack with collapsible explanation ──────────────────────────────────
// Extracted to its own component so it can own expand/collapse state via useState.
//
// `forPdf=true` forces the card open and hides the "Learn more" toggle so the
// captured PDF shows the full helper text — without this, the off-screen
// html2canvas snapshot picks up the collapsed "summary…" state and the
// printed PDF reads as "to me. I have…" / "the advan…" (the bug reported on
// 2026-05-29). The on-screen interactive flow is unaffected.
function ToggleAckField({
    id, label, requiredIndicator, isOn, ackHelperText, isHtml,
    summaryText, needsExpand, ackPrimaryColor, error, effectiveReadOnly,
    onChange, theme, fieldStyle, renderError, forPdf,
}) {
    const [expanded, setExpanded] = useState(false);
    // When rendering into the PDF snapshot, always show full text.
    const isExpanded = forPdf || expanded;

    return (
        <Box data-field-id={id} data-pdf-block="toggle-ack" sx={{
            borderRadius: '12px', overflow: 'hidden',
            border: `0.5px solid ${error ? '#FF4842' : isOn ? '#5DCAA5' : 'rgba(0,0,0,0.12)'}`,
            transition: 'border-color 0.25s ease',
            mb: 1.5,
        }}>
            {/* Toggle header — label + switch, always visible */}
            <Box
                sx={{
                    px: 2, py: 1.5,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 1.5,
                    cursor: effectiveReadOnly ? 'default' : 'pointer',
                    bgcolor: isOn ? '#E1F5EE' : 'rgba(0,0,0,0.02)',
                    transition: 'background 0.25s, border-color 0.25s',
                }}
                onClick={() => { if (!effectiveReadOnly) onChange(id, !isOn); }}
            >
                {/* Checkmark + label */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1, minWidth: 0 }}>
                    {isOn && (
                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" style={{ flexShrink: 0 }}>
                            <polyline points="20 6 9 17 4 12" stroke={ackPrimaryColor} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                    )}
                    <Typography
                        variant="body2"
                        sx={{
                            fontSize: '13px',
                            color: isOn ? '#0F6E56' : '#444',
                            fontFamily: theme?.fontFamily || 'inherit',
                            fontWeight: isOn ? 500 : 500,
                            lineHeight: 1.5,
                            userSelect: 'none',
                            transition: 'color 0.2s',
                        }}
                    >
                        {label}{requiredIndicator}
                    </Typography>
                </Box>

                {/* Yes/No label + toggle switch */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
                    <Typography sx={{ fontSize: '11px', color: isOn ? '#0F6E56' : '#aaa', userSelect: 'none' }}>
                        {isOn ? 'Yes' : 'No'}
                    </Typography>
                    <Box
                        component="button"
                        type="button"
                        role="switch"
                        aria-checked={isOn}
                        aria-label={label}
                        disabled={effectiveReadOnly}
                        onClick={(e) => { e.stopPropagation(); if (!effectiveReadOnly) onChange(id, !isOn); }}
                        sx={{
                            position: 'relative', display: 'inline-flex', flexShrink: 0, border: 0, padding: 0,
                            '&:focus-visible': { outline: '2px solid', outlineColor: ackPrimaryColor, outlineOffset: 4 },
                            width: 48, height: 44, borderRadius: '13px', bgcolor: 'transparent',
                            '&::before': { content: '""', position: 'absolute', top: 9, left: 0, width: 48, height: 26, borderRadius: '13px', bgcolor: isOn ? ackPrimaryColor : 'rgba(0,0,0,0.15)' },
                            transition: 'background 0.25s',
                            cursor: effectiveReadOnly ? 'default' : 'pointer',
                            WebkitTapHighlightColor: 'transparent',
                        }}
                    >
                        <Box sx={{
                            position: 'absolute',
                            width: 20, height: 20, borderRadius: '50%', bgcolor: '#fff',
                            boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
                            top: 12,
                            left: isOn ? 25 : 3,
                            transition: 'left 0.25s',
                        }} />
                    </Box>
                </Box>
            </Box>

            {/* Collapsible explanation body */}
            {ackHelperText && (
                <Box sx={{
                    borderTop: `0.5px solid ${isOn ? '#9FE1CB' : 'rgba(0,0,0,0.06)'}`,
                }}>
                    {/* Summary line — always visible */}
                    <Box sx={{ px: 2, pt: 1.25, pb: isExpanded ? 0 : 1.25 }}>
                        <Typography
                            variant="body2"
                            component="div"
                            sx={{
                                fontSize: '12.5px',
                                color: fieldStyle.textColor || '#777',
                                fontFamily: theme?.fontFamily || 'inherit',
                                lineHeight: 1.6,
                            }}
                        >
                            {isExpanded ? null : summaryText}
                        </Typography>
                    </Box>

                    {/* Expanded full text */}
                    {isExpanded && (
                        <Box sx={{ px: 2, pt: 0.5, pb: 1.25 }}>
                            <Typography
                                variant="body2"
                                component="div"
                                sx={{
                                    fontSize: '13px',
                                    color: fieldStyle.textColor || '#555',
                                    fontFamily: theme?.fontFamily || 'inherit',
                                    lineHeight: 1.7,
                                }}
                                dangerouslySetInnerHTML={isHtml ? { __html: ackHelperText } : undefined}
                            >
                                {isHtml ? undefined : ackHelperText}
                            </Typography>
                        </Box>
                    )}

                    {/* Learn more / Show less toggle — hidden in PDF mode */}
                    {needsExpand && !forPdf && (
                        <Box
                            sx={{ px: 2, pb: 1.25, pt: 0.25 }}
                            onClick={(e) => { e.stopPropagation(); setExpanded(!expanded); }}
                        >
                            <Typography
                                sx={{
                                    fontSize: '12px',
                                    color: ackPrimaryColor,
                                    fontWeight: 600,
                                    cursor: 'pointer',
                                    userSelect: 'none',
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: 0.5,
                                    '&:hover': { textDecoration: 'underline' },
                                }}
                            >
                                {isExpanded ? 'Show less' : 'Learn more'}
                                <Box
                                    component="span"
                                    sx={{
                                        display: 'inline-block',
                                        transition: 'transform 0.2s',
                                        transform: isExpanded ? 'rotate(180deg)' : 'rotate(0)',
                                        fontSize: '14px',
                                        lineHeight: 1,
                                    }}
                                >
                                    ▾
                                </Box>
                            </Typography>
                        </Box>
                    )}
                </Box>
            )}
            {renderError(error)}
        </Box>
    );
}

ToggleAckField.propTypes = {
    id: PropTypes.string, label: PropTypes.string, requiredIndicator: PropTypes.node,
    isOn: PropTypes.bool, ackHelperText: PropTypes.string, isHtml: PropTypes.bool,
    summaryText: PropTypes.string, needsExpand: PropTypes.bool, ackPrimaryColor: PropTypes.string,
    error: PropTypes.string, effectiveReadOnly: PropTypes.bool,
    onChange: PropTypes.func, theme: PropTypes.object, fieldStyle: PropTypes.object,
    renderError: PropTypes.func, forPdf: PropTypes.bool,
};
ToggleAckField.defaultProps = { forPdf: false };

const ConsentFieldRenderer = ({ field, value, onChange, error, readOnly, theme, onBlur, forPdf }) => {
    const { id, type, label, placeholder, helper_text, default_value, options, required, metadata } = field;

    const fallbackInputId = useId();
    const fieldStyle = metadata?.style || {};

    // ── System Auto-Fill ──
    const autoFillSource = metadata?.auto_fill;
    const isLockedWhenFilled = metadata?.locked_when_filled;
    const [systemAutoFilled, setSystemAutoFilled] = useState(false);

    useEffect(() => {
        if (systemAutoFilled || value) return;

        let autoValue = null;
        switch (autoFillSource) {
            case 'today_date':
                autoValue = formatDateForStorage(new Date());
                break;
            case 'current_time':
                autoValue = new Date().toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
                break;
            case 'current_datetime':
                autoValue = `${new Date().toLocaleDateString('en-GB')} ${new Date().toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}`;
                break;
            case 'practice_name':
                autoValue = theme?.practiceName || '';
                break;
            case 'form_name':
                autoValue = theme?.formName || '';
                break;
            default:
                // Legacy: auto_today support
                if (metadata?.auto_today && type === CONSENT_FIELD_TYPES.DATE) {
                    autoValue = formatDateForStorage(new Date());
                }
                break;
        }

        if (autoValue) {
            onChange(id, autoValue);
            setSystemAutoFilled(true);
        }
    }, [autoFillSource, value, systemAutoFilled, type, id, onChange, metadata?.auto_today, theme]);

    // Determine if field should be locked (read-only)
    const isFieldLocked = isLockedWhenFilled && !!(value || field.lead_mapping);
    const effectiveReadOnly = readOnly || isFieldLocked;
    // Historical submissions stored boolean answers as strings. Interpret only
    // explicit true spellings for display; editable acknowledgements stay strict.
    const acknowledgedForDisplay = value === true
        || ((readOnly || forPdf) && (value === 'True' || value === 'true'));
    const autofillProps = consentAutofillProps(field, { inactive: effectiveReadOnly || forPdf, fallbackId: fallbackInputId });

    // ── isFilledByPractice flag — evaluated here but early return is after all hooks ──
    const isFilledByPractice = metadata?.filled_by === 'practice';

    // ── Field tooltip (help-on-demand, distinct from always-visible helper_text) ──
    const tooltipText = metadata?.tooltip_text;
    const [tooltipOpen, setTooltipOpen] = useState(false);

    // ── Theme-aware styling (matching builder's PreviewField exactly) ──

    const labelSx = {
        mb: 0.75,
        fontWeight: fieldStyle.labelBold ? 700 : 600,
        color: fieldStyle.labelColor || theme?.labelColor || '#212B36',
        fontSize: fieldStyle.labelFontSize ? `${fieldStyle.labelFontSize}px` : (theme?.labelFontSize || '13px'),
        fontFamily: theme?.fontFamily || 'inherit',
        textAlign: fieldStyle.textAlign || undefined,
        letterSpacing: '-0.01em',
    };

    const primaryColor = theme?.primaryColor || '#00AB55';
    const primaryLight = `${primaryColor}14`;
    const inputSx = {
        '& .MuiOutlinedInput-root': {
            borderRadius: fieldStyle.borderRadius ? `${fieldStyle.borderRadius}px` : (theme?.inputBorderRadius || '10px'),
            bgcolor: effectiveReadOnly ? '#F9FAFB' : (fieldStyle.backgroundColor || theme?.inputBackgroundColor || '#F9FAFB'),
            fontSize: { xs: '16px', sm: '14px' }, // 16px on mobile prevents iOS auto-zoom
            minHeight: '48px',
            transition: 'all 0.2s ease',
            '& fieldset': {
                borderColor: fieldStyle.borderColor || theme?.inputBorderColor || 'rgba(145,158,171,0.16)',
                transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
            },
            '&:hover fieldset': {
                borderColor: effectiveReadOnly ? 'rgba(145,158,171,0.16)' : 'rgba(145,158,171,0.4)',
            },
            '&.Mui-focused': {
                bgcolor: '#fff',
            },
            '&.Mui-focused fieldset': {
                borderColor: theme?.inputFocusBorderColor || primaryColor,
                borderWidth: '2px',
                boxShadow: `0 0 0 3px ${primaryLight}`,
            },
        },
        '& .MuiOutlinedInput-input': {
            py: '12px',
            px: '14px',
            fontSize: { xs: '16px', sm: '14px' },
            '&::placeholder': {
                color: '#C4CDD5',
                opacity: 1,
            },
        },
    };

    const bodySx = {
        color: fieldStyle.textColor || theme?.bodyColor || 'text.secondary',
        fontSize: fieldStyle.fontSize ? `${fieldStyle.fontSize}px` : (theme?.bodyFontSize || '0.875rem'),
        fontFamily: theme?.fontFamily || 'inherit',
        fontWeight: fieldStyle.fontWeight || undefined,
        fontStyle: fieldStyle.fontStyle || undefined,
        textAlign: fieldStyle.textAlign || undefined,
        textTransform: fieldStyle.textTransform || undefined,
        lineHeight: fieldStyle.lineHeight || undefined,
        letterSpacing: fieldStyle.letterSpacing ? `${fieldStyle.letterSpacing}px` : undefined,
    };

    const requiredIndicator = required
        ? (theme?.requiredIndicatorStyle === 'text'
            ? <span style={{ color: '#FF4842', fontWeight: 500, fontSize: '0.75em' }}> (Required)</span>
            : <span style={{ color: '#FF4842' }}> *</span>)
        : null;

    const renderLabel = () => {
        if (metadata?.hide_label) return null;
        return (
            <Typography variant="body2" sx={{ ...labelSx, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                {label}{requiredIndicator}
                {isFieldLocked && (
                    <Box component="span" sx={{ display: 'inline-flex', color: 'text.disabled', ml: 0.5 }}>
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor">
                            <path d="M18 8h-1V6c0-2.76-2.24-5-5-5S7 3.24 7 6v2H6c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V10c0-1.1-.9-2-2-2zm-6 9c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2zm3.1-9H8.9V6c0-1.71 1.39-3.1 3.1-3.1 1.71 0 3.1 1.39 3.1 3.1v2z" />
                        </svg>
                    </Box>
                )}
                {tooltipText && (
                    <ClickAwayListener onClickAway={() => setTooltipOpen(false)}>
                        <Tooltip
                            title={tooltipText}
                            open={tooltipOpen}
                            onClose={() => setTooltipOpen(false)}
                            disableFocusListener
                            disableHoverListener
                            disableTouchListener
                            placement="top"
                            arrow
                            PopperProps={{ sx: { '& .MuiTooltip-tooltip': { maxWidth: 280, fontSize: '0.78rem', lineHeight: 1.5 } } }}
                        >
                            <Box
                                component="span"
                                role="button"
                                tabIndex={0}
                                aria-label="More information"
                                onClick={(e) => { e.stopPropagation(); setTooltipOpen((o) => !o); }}
                                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setTooltipOpen((o) => !o); } }}
                                sx={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    width: 16, height: 16,
                                    borderRadius: '50%',
                                    bgcolor: 'rgba(145,158,171,0.16)',
                                    color: 'text.secondary',
                                    cursor: 'pointer',
                                    ml: 0.25,
                                    flexShrink: 0,
                                    transition: 'background-color 0.2s',
                                    '&:hover': { bgcolor: 'rgba(145,158,171,0.32)' },
                                }}
                            >
                                <svg width="10" height="10" viewBox="0 0 24 24" fill="currentColor">
                                    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-6h2v6zm0-8h-2V7h2v2z" />
                                </svg>
                            </Box>
                        </Tooltip>
                    </ClickAwayListener>
                )}
            </Typography>
        );
    };

    const renderHelperText = () => {
        if (!helper_text) return null;
        return (
            <Typography variant="caption" sx={{ color: 'text.secondary', fontFamily: theme?.fontFamily || 'inherit' }}>
                {helper_text}
            </Typography>
        );
    };

    const renderError = (errMsg) => {
        if (!errMsg) return null;
        return (
            <Box sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.75,
                mt: 0.75,
                px: 1.25,
                py: 0.5,
                borderRadius: '6px',
                bgcolor: 'rgba(255, 72, 66, 0.06)',
                border: '1px solid rgba(255, 72, 66, 0.16)',
            }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="#FF4842" style={{ flexShrink: 0 }}>
                    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z" />
                </svg>
                <Typography variant="caption" sx={{ color: '#B72136', fontWeight: 500, fontSize: '0.75rem', lineHeight: 1.4 }}>
                    {errMsg}
                </Typography>
            </Box>
        );
    };

    const handleChange = (e) => {
        onChange(id, e.target.value);
    };

    const handleCheckChange = (e) => {
        onChange(id, e.target.checked);
    };

    const handleBlur = useCallback(() => {
        if (onBlur) onBlur(id, value);
    }, [onBlur, id, value]);

    // ── Practice-filled read-only card ─────────────────────────────────────
    // Fields with metadata.filled_by === 'practice' are pre-filled by the clinic
    // and render as styled read-only display cards in the patient-facing view.
    // This early return must come AFTER all hooks above to satisfy react-hooks rules.
    if (isFilledByPractice) {
        const displayValue = value ?? default_value ?? '';
        const hasValue = displayValue !== '' && displayValue !== null && displayValue !== undefined;
        return (
            <Box
                data-field-id={id}
                sx={{
                    bgcolor: '#F4F6F8',
                    border: '1px solid #DFE3E8',
                    borderRadius: '8px',
                    px: 2,
                    py: 1.5,
                    opacity: 0.9,
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: hasValue ? 0.5 : 0 }}>
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="#919EAB" style={{ flexShrink: 0 }}>
                        <path d="M18 8h-1V6c0-2.76-2.24-5-5-5S7 3.24 7 6v2H6c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V10c0-1.1-.9-2-2-2zm-6 9c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2zm3.1-9H8.9V6c0-1.71 1.39-3.1 3.1-3.1 1.71 0 3.1 1.39 3.1 3.1v2z" />
                    </svg>
                    <Typography
                        variant="caption"
                        sx={{ color: '#637381', fontWeight: 600, fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}
                    >
                        {label}
                        <Box component="span" sx={{ ml: 0.75, fontWeight: 400, textTransform: 'none', color: '#919EAB' }}>
                            {'\u2014'} completed by your dental team
                        </Box>
                    </Typography>
                </Box>
                {hasValue && (
                    <Typography
                        variant="body2"
                        sx={{
                            color: '#212B36',
                            fontSize: theme?.bodyFontSize || '0.875rem',
                            fontFamily: theme?.fontFamily || 'inherit',
                            whiteSpace: 'pre-wrap',
                            wordBreak: 'break-word',
                            mt: 0.25,
                        }}
                    >
                        {String(displayValue)}
                    </Typography>
                )}
                {!hasValue && (
                    <Typography variant="caption" sx={{ color: '#B0BEC5', fontStyle: 'italic', fontSize: '0.8rem' }}>
                        To be completed by the practice
                    </Typography>
                )}
            </Box>
        );
    }

    // ── PDF-mode value coalescing ────────────────────────────────────────
    // When the field is empty AND we're rendering for the PDF snapshot,
    // substitute an em-dash so the rasterised pill is never visually
    // empty (matches the ReportLab fallback's "— for unanswered" rule).
    // The live interactive flow is unaffected — empty inputs still look
    // empty to the patient.
    const coalesceForPdf = (raw) => {
        if (!forPdf) return raw;
        if (raw === null || raw === undefined || raw === '') return '—';
        return raw;
    };

    switch (type) {
        case CONSENT_FIELD_TYPES.TEXT:
        case CONSENT_FIELD_TYPES.EMAIL:
        case CONSENT_FIELD_TYPES.PHONE:
        case CONSENT_FIELD_TYPES.NUMBER:
            return (
                <Box data-field-id={id}>
                    {renderLabel()}
                    <TextField
                        size="small"
                        fullWidth
                        placeholder={forPdf ? '' : (placeholder || label)}
                        value={coalesceForPdf(value ?? default_value ?? '')}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={Boolean(error)}
                        InputProps={{ readOnly: effectiveReadOnly, inputProps: autofillProps }}
                        type={
                            forPdf
                                ? 'text' // avoid number/email/tel rendering quirks during raster capture
                                : (type === CONSENT_FIELD_TYPES.NUMBER ? 'number'
                                    : type === CONSENT_FIELD_TYPES.EMAIL ? 'email'
                                        : type === CONSENT_FIELD_TYPES.PHONE ? 'tel'
                                            : 'text')
                        }
                        sx={inputSx}
                    />
                    {renderHelperText()}
                    {renderError(error)}
                </Box>
            );

        case CONSENT_FIELD_TYPES.TEXTAREA:
            return (
                <Box data-field-id={id}>
                    {renderLabel()}
                    <TextField
                        size="small"
                        fullWidth
                        multiline
                        rows={undefined}
                        placeholder={forPdf ? '' : (placeholder || label)}
                        value={coalesceForPdf(value ?? default_value ?? '')}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={Boolean(error)}
                        InputProps={{ readOnly: effectiveReadOnly, inputProps: autofillProps }}
                        sx={{
                            ...inputSx,
                            '& .MuiOutlinedInput-root': {
                                ...inputSx['& .MuiOutlinedInput-root'],
                                minHeight: fieldStyle.height ? `${fieldStyle.height}px` : 120,
                            },
                        }}
                    />
                    {renderHelperText()}
                    {renderError(error)}
                </Box>
            );

        case CONSENT_FIELD_TYPES.PARAGRAPH_DISPLAY:
            return (
                <Box>
                    {label && required && (
                        <Typography variant="body2" sx={{ ...labelSx, mb: 0.5 }}>
                            {label}{requiredIndicator}
                        </Typography>
                    )}
                    <Typography
                        variant="body2"
                        sx={{
                            ...bodySx,
                            lineHeight: 1.7,
                            whiteSpace: 'pre-wrap',
                        }}
                    >
                        {metadata?.content || default_value || label}
                    </Typography>
                </Box>
            );

        case CONSENT_FIELD_TYPES.HEADING: {
            const headingLevel = metadata?.heading_level || 'h3';
            const variant = HEADING_VARIANT_MAP[headingLevel] || 'h5';
            // Font size cascade: field override > theme typography scale > legacy theme heading size > built-in defaults
            const HEADING_SIZE_DEFAULTS = { h1: 32, h2: 28, h3: 24, h4: 20, h5: 18, h6: 16 };
            const themeScaleKey = `typography${headingLevel.charAt(0).toUpperCase()}${headingLevel.charAt(1)}`;
            const themeScaleSize = theme?.[themeScaleKey];
            const themeScaleColor = theme?.[`${themeScaleKey}Color`];
            const resolvedFontSize = fieldStyle.fontSize
                ? `${fieldStyle.fontSize}px`
                : (fieldStyle.labelFontSize
                    ? `${fieldStyle.labelFontSize}px`
                    : (themeScaleSize
                        ? `${themeScaleSize}px`
                        : (theme?.headingFontSize
                            ? `${theme.headingFontSize}px`
                            : `${HEADING_SIZE_DEFAULTS[headingLevel] || 20}px`)));
            return (
                <Typography
                    variant={variant}
                    sx={{
                        '&&': {
                            color: fieldStyle.textColor || fieldStyle.labelColor || themeScaleColor || theme?.headingColor || 'text.primary',
                            fontSize: resolvedFontSize,
                            fontFamily: theme?.headingFontFamily || theme?.fontFamily || 'inherit',
                            fontWeight: fieldStyle.fontWeight || (fieldStyle.labelBold ? 700 : undefined),
                            fontStyle: fieldStyle.fontStyle || undefined,
                            textAlign: fieldStyle.textAlign || undefined,
                            textTransform: fieldStyle.textTransform || undefined,
                            lineHeight: fieldStyle.lineHeight || undefined,
                            letterSpacing: fieldStyle.letterSpacing ? `${fieldStyle.letterSpacing}px` : undefined,
                            backgroundColor: fieldStyle.backgroundColor || undefined,
                        },
                    }}
                >
                    {label || default_value}
                </Typography>
            );
        }

        case CONSENT_FIELD_TYPES.DIVIDER:
            return <Divider sx={{ my: 2 }} />;

        case CONSENT_FIELD_TYPES.IMAGE: {
            const rawImageUrl = metadata?.image_url || default_value;
            const imageHeight = fieldStyle.height || metadata?.image_height || 120;
            const imageAlignment = metadata?.image_alignment || 'center';
            // Resolve image URL: media: prefix → strip it, http(s) → use as-is, fallback → as-is
            let imageUrl = rawImageUrl;
            if (rawImageUrl?.startsWith('media:')) {
                imageUrl = rawImageUrl.replace('media:', '');
            }
            return (
                <Box sx={{ textAlign: imageAlignment, width: '100%' }}>
                    {imageUrl ? (
                        <Box
                            component="img"
                            src={imageUrl}
                            alt={label || 'Form image'}
                            sx={{
                                maxWidth: '100%',
                                height: imageHeight,
                                objectFit: 'contain',
                            }}
                        />
                    ) : (
                        <Box sx={{
                            width: '100%', height: 60,
                            bgcolor: 'rgba(145,158,171,0.12)',
                            borderRadius: fieldStyle.borderRadius ? `${fieldStyle.borderRadius}px` : '8px',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                        }}>
                            <Typography variant="caption" color="text.disabled">Image</Typography>
                        </Box>
                    )}
                </Box>
            );
        }

        case CONSENT_FIELD_TYPES.CHECKBOX: {
            const cbText = field.metadata?.content || label;
            const cbAlign = field.metadata?.checkboxAlignment || 'top';
            return (
                <Box data-field-id={id}>
                    <FormControlLabel
                        control={
                            <Checkbox
                                size="small"
                                checked={acknowledgedForDisplay}
                                onChange={handleCheckChange}
                                disabled={effectiveReadOnly}
                                sx={{
                                    '&.Mui-checked': { color: theme?.primaryColor || '#078DEE' },
                                    ...(cbAlign === 'top' && { mt: '-2px' }),
                                    ...(error && {
                                        color: '#FF4842',
                                        '&.Mui-checked': { color: '#FF4842' },
                                    }),
                                }}
                            />
                        }
                        label={
                            <Typography variant="body2" sx={{ ...bodySx, color: fieldStyle.labelColor || theme?.labelColor || 'text.primary' }}>
                                {cbText}{requiredIndicator}
                            </Typography>
                        }
                        sx={{
                            ...(error && { color: '#FF4842' }),
                            ...(cbAlign === 'top' && { alignItems: 'flex-start' }),
                        }}
                    />
                    {renderError(error)}
                </Box>
            );
        }

        case CONSENT_FIELD_TYPES.CHECKBOX_GROUP:
            return (
                <Box data-field-id={id}>
                    <ConsentCheckboxGroup
                        field={field}
                        value={value}
                        onChange={onChange}
                        error={error}
                        readOnly={effectiveReadOnly}
                        theme={theme}
                    />
                </Box>
            );

        case CONSENT_FIELD_TYPES.RADIO: {
            const radioLayout = metadata?.layout;

            // Inline radios render as pill-style buttons for better mobile UX
            if (radioLayout === 'inline') {
                return (
                    <Box data-field-id={id}>
                        {renderLabel()}
                        <Box sx={{
                            display: 'flex', flexWrap: 'wrap', gap: 1, mt: 0.5,
                            // 2-column grid on narrow screens
                            '@media (max-width: 480px)': {
                                display: 'grid',
                                gridTemplateColumns: 'repeat(2, 1fr)',
                            },
                        }}>
                            {(options || []).map((opt) => {
                                const isSelected = (value || '') === opt.value;
                                return (
                                    <Box
                                        key={opt.value}
                                        role="radio"
                                        aria-checked={isSelected}
                                        tabIndex={0}
                                        onClick={() => { if (!effectiveReadOnly) onChange(id, isSelected ? '' : opt.value); }}
                                        onKeyDown={(e) => { if (!effectiveReadOnly && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); onChange(id, isSelected ? '' : opt.value); } }}
                                        sx={{
                                            px: '18px', py: '10px',
                                            minHeight: '48px',
                                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                                            borderRadius: '20px',
                                            border: `1.5px solid ${error ? '#FF4842' : isSelected ? primaryColor : '#DFE3E8'}`,
                                            bgcolor: isSelected ? primaryColor : '#fff',
                                            color: isSelected ? '#fff' : '#637381',
                                            fontSize: '13px', fontFamily: theme?.fontFamily || 'inherit',
                                            fontWeight: isSelected ? 600 : 400,
                                            cursor: effectiveReadOnly ? 'default' : 'pointer',
                                            transition: 'all 0.15s ease',
                                            userSelect: 'none',
                                            boxShadow: isSelected ? `0 2px 8px ${primaryLight}` : 'none',
                                            '&:hover': effectiveReadOnly ? {} : {
                                                bgcolor: isSelected ? primaryColor : `${primaryColor}08`,
                                                borderColor: primaryColor,
                                            },
                                        }}
                                    >
                                        {isSelected && (
                                            <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" style={{ marginRight: 6, flexShrink: 0 }}>
                                                <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z" />
                                            </svg>
                                        )}
                                        {opt.label || opt.value}
                                    </Box>
                                );
                            })}
                        </Box>
                        {renderError(error)}
                    </Box>
                );
            }

            // Default stacked radio layout
            return (
                <Box data-field-id={id}>
                    {renderLabel()}
                    <RadioGroup
                        value={value || ''}
                        onChange={handleChange}
                    >
                        {(options || []).map((opt) => (
                            <FormControlLabel
                                key={opt.value}
                                value={opt.value}
                                sx={{ alignItems: 'center', ml: 0 }}
                                control={
                                    <Radio
                                        size="small"
                                        disabled={effectiveReadOnly}
                                        sx={{
                                            '&.Mui-checked': { color: primaryColor },
                                        }}
                                    />
                                }
                                label={<Typography variant="body2" sx={{ ...bodySx, lineHeight: 1.4 }}>{opt.label || opt.value}</Typography>}
                            />
                        ))}
                    </RadioGroup>
                    {renderError(error)}
                </Box>
            );
        }

        case CONSENT_FIELD_TYPES.SELECT: {
            // Loose match: lead prefill / previous submission values may differ
            // in case or whitespace from the option's canonical ``value`` (e.g.
            // BE stores salutation as ``MR`` but a legacy normalizer may have
            // lowercased it to ``mr``). MUI Select renders empty if the value
            // doesn't strictly equal any MenuItem value, but the unmatched
            // value still submits — making the field look empty while passing
            // required-field validation. Resolve the incoming value against
            // ``options`` case-insensitively / trim-insensitively and fall
            // back to the literal value if no option matches.
            const normalizeForMatch = (v) => (v ?? '').toString().trim().toLowerCase();
            const matchedOption = (options || []).find(
                (opt) => normalizeForMatch(opt.value) === normalizeForMatch(value)
            );
            const selectValue = matchedOption ? matchedOption.value : (value || '');
            return (
                <Box data-field-id={id}>
                    {renderLabel()}
                    <Select
                        inputProps={autofillProps}
                        size="small"
                        fullWidth
                        displayEmpty
                        value={selectValue}
                        onChange={handleChange}
                        onClose={handleBlur}
                        error={Boolean(error)}
                        disabled={effectiveReadOnly}
                        sx={{
                            borderRadius: fieldStyle.borderRadius ? `${fieldStyle.borderRadius}px` : (theme?.inputBorderRadius || '10px'),
                            bgcolor: effectiveReadOnly ? '#F9FAFB' : (fieldStyle.backgroundColor || theme?.inputBackgroundColor || '#F9FAFB'),
                            fontSize: '14px',
                            transition: 'all 0.2s ease',
                            '& fieldset': {
                                borderColor: fieldStyle.borderColor || theme?.inputBorderColor || 'rgba(145,158,171,0.16)',
                            },
                            '&:hover fieldset': {
                                borderColor: effectiveReadOnly ? 'rgba(145,158,171,0.16)' : 'rgba(145,158,171,0.4)',
                            },
                            '&.Mui-focused fieldset': {
                                borderColor: primaryColor,
                                borderWidth: '1.5px',
                            },
                            '& .MuiSelect-select': {
                                py: '12px',
                                px: '14px',
                            },
                        }}
                        MenuProps={{
                            PaperProps: {
                                sx: {
                                    borderRadius: '12px',
                                    boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                                    mt: 0.5,
                                    '& .MuiMenuItem-root': {
                                        fontSize: '14px',
                                        borderRadius: '8px',
                                        mx: 0.75,
                                        '&.Mui-selected': {
                                            bgcolor: `${primaryColor}14`,
                                            fontWeight: 600,
                                        },
                                    },
                                },
                            },
                        }}
                    >
                        <MenuItem value="" sx={{ color: '#C4CDD5' }}>{placeholder || 'Select...'}</MenuItem>
                        {(options || []).map((opt) => (
                            <MenuItem key={opt.value} value={opt.value}>{opt.label || opt.value}</MenuItem>
                        ))}
                    </Select>
                    {renderError(error)}
                </Box>
            );
        }

        case CONSENT_FIELD_TYPES.DATE: {
            // In PDF mode, swap the interactive DatePicker (which rasterises
            // its calendar-icon and dotted-underline placeholder as visual
            // noise) for a plain read-only TextField — clean pill, real
            // value, no calendar glyph.
            if (forPdf) {
                const parsed = parseDateValue(value);
                const displayText = (parsed && !Number.isNaN(parsed.getTime()))
                    ? `${String(parsed.getDate()).padStart(2, '0')}/${String(parsed.getMonth() + 1).padStart(2, '0')}/${parsed.getFullYear()}`
                    : '—';
                return (
                    <Box data-field-id={id}>
                        {renderLabel()}
                        <TextField
                            size="small"
                            fullWidth
                            value={displayText}
                            InputProps={{ readOnly: true, inputProps: autofillProps }}
                            sx={inputSx}
                        />
                        {renderHelperText()}
                        {renderError(error)}
                    </Box>
                );
            }
            return (
                <Box data-field-id={id}>
                    {renderLabel()}
                    <DatePicker
                        value={parseDateValue(value)}
                        onChange={(newDate) => {
                            if (!effectiveReadOnly) onChange(id, formatDateForStorage(newDate));
                        }}
                        onClose={handleBlur}
                        format="dd/MM/yyyy"
                        disabled={effectiveReadOnly}
                        slotProps={{
                            popper: {
                                sx: { zIndex: 1500 },
                            },
                            textField: {
                                size: 'small',
                                fullWidth: true,
                                placeholder: placeholder || 'DD/MM/YYYY',
                                inputProps: autofillProps,
                                error: Boolean(error),
                                sx: inputSx,
                                onKeyDown: (e) => e.preventDefault(),
                            },
                        }}
                    />
                    {renderHelperText()}
                    {renderError(error)}
                </Box>
            );
        }

        case CONSENT_FIELD_TYPES.SIGNATURE:
            return (
                <Box data-field-id={id}>
                    {renderLabel()}
                    <ConsentSignaturePad
                        fieldId={id}
                        label={label}
                        value={value}
                        onChange={onChange}
                        error={error}
                        required={required}
                        allowDrawn={metadata?.allow_drawn !== false}
                        allowTyped={metadata?.allow_typed !== false}
                        readOnly={effectiveReadOnly}
                        theme={theme}
                        borderStyle={fieldStyle.signatureBorderStyle || 'solid'}
                        borderColor={fieldStyle.signatureBorderColor || 'rgba(145,158,171,0.32)'}
                        borderWidth={fieldStyle.signatureBorderWidth || 2}
                        borderRadius={fieldStyle.signatureBorderRadius || 8}
                        backgroundColor={fieldStyle.signatureBackgroundColor || '#fff'}
                    />
                    {renderError(error)}
                </Box>
            );

        case CONSENT_FIELD_TYPES.HTML: {
            const htmlContent = normalizeQuillHtml(default_value || '');
            const displayStyle = metadata?.display_style;
            if (displayStyle === 'info_block') {
                return (
                    <Box>
                        {!metadata?.hide_label && renderLabel()}
                        <Box
                            sx={{
                                background: '#f8f9fa',
                                borderLeft: `3px solid ${theme?.primaryColor || '#4a90d9'}`,
                                padding: '16px 20px',
                                borderRadius: '0 5px 5px 0',
                                fontSize: theme?.bodyFontSize ? `${theme.bodyFontSize}px` : '0.9rem',
                                color: '#444',
                                fontFamily: theme?.fontFamily || 'inherit',
                                lineHeight: 1.6,
                                '& p': { margin: 0, '&:not(:last-child)': { marginBottom: '0.5rem' } },
                            }}
                            dangerouslySetInnerHTML={{ __html: htmlContent }}
                        />
                    </Box>
                );
            }
            return (
                <Box>
                    {renderLabel()}
                    <HtmlContentWrapper
                        sx={{
                            ...bodySx,
                            lineHeight: 1.7,
                        }}
                        dangerouslySetInnerHTML={{ __html: htmlContent }}
                    />
                </Box>
            );
        }

        case CONSENT_FIELD_TYPES.TOGGLE_ACK: {
            const isOn = acknowledgedForDisplay;
            const ackHelperText = helper_text || metadata?.content || '';
            const ackPrimaryColor = theme?.primaryColor || '#00AB55';
            const isHtml = ackHelperText.includes('<');
            // Strip HTML tags for plain-text summary
            const plainText = isHtml ? ackHelperText.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim() : ackHelperText;
            const SUMMARY_LENGTH = 120;
            const needsExpand = plainText.length > SUMMARY_LENGTH;
            const summaryText = needsExpand ? `${plainText.slice(0, SUMMARY_LENGTH).trim()}…` : plainText;

            return (
                <ToggleAckField
                    id={id}
                    label={label}
                    requiredIndicator={requiredIndicator}
                    isOn={isOn}
                    ackHelperText={ackHelperText}
                    isHtml={isHtml}
                    summaryText={summaryText}
                    needsExpand={needsExpand}
                    ackPrimaryColor={ackPrimaryColor}
                    error={error}
                    effectiveReadOnly={effectiveReadOnly}
                    onChange={onChange}
                    theme={theme}
                    fieldStyle={fieldStyle}
                    renderError={renderError}
                    forPdf={forPdf}
                />
            );
        }

        case CONSENT_FIELD_TYPES.TOGGLE_DETAIL: {
            const tdValue = value || {};
            const isToggled = tdValue.toggled === true;
            const isDeclined = tdValue.toggled === 'declined';
            const selectedPills = tdValue.selected || [];
            const detailsText = tdValue.details || '';
            const pillOptions = options || [];
            const showDetails = metadata?.show_details_field !== false;
            const detailsPlaceholder = metadata?.details_placeholder || 'Enter details...';
            const allowDecline = metadata?.allow_decline === true;

            if (isDeclined) {
                return (
                    <Box data-field-id={id} sx={{
                        border: '1px solid #DFE3E8', borderRadius: '12px', p: 2,
                        bgcolor: '#fafafa',
                    }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
                            <Typography variant="body2" sx={{ fontSize: '13px', color: '#919EAB', fontStyle: 'italic', fontFamily: theme?.fontFamily || 'inherit' }}>
                                {label} — <em>Declined to answer</em>
                            </Typography>
                            {!effectiveReadOnly && (
                                <Typography
                                    variant="caption"
                                    sx={{ color: '#078DEE', cursor: 'pointer', ml: 1 }}
                                    onClick={() => onChange(id, {})}
                                >
                                    Undo
                                </Typography>
                            )}
                        </Box>
                    </Box>
                );
            }

            return (
                <Box data-field-id={id} sx={{
                    border: `1px solid ${error ? '#FF4842' : isToggled ? primaryColor : '#F2F3F5'}`,
                    borderRadius: '12px',
                    bgcolor: isToggled ? '#F0FFF4' : '#fff',
                    transition: 'all 0.2s',
                    overflow: 'hidden',
                }}>
                    {/* Header row */}
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 2 }}>
                        <Typography variant="body2" sx={{
                            flex: 1, fontSize: '13px', fontWeight: 500,
                            color: fieldStyle.textColor || theme?.bodyColor || '#212B36',
                            fontFamily: theme?.fontFamily || 'inherit',
                            userSelect: 'none',
                        }}>
                            {label}{requiredIndicator}
                        </Typography>

                        {/* Count badge — visible when toggled ON and pills are selected */}
                        {isToggled && selectedPills.length > 0 && (
                            <Box sx={{
                                px: 1,
                                py: '2px',
                                borderRadius: 10,
                                bgcolor: primaryColor,
                                color: '#fff',
                                fontSize: '11px',
                                fontWeight: 600,
                                mx: 1,
                                flexShrink: 0,
                                lineHeight: 1.6,
                            }}>
                                {selectedPills.length} selected
                            </Box>
                        )}

                        {/* Toggle switch */}
                        <Box
                            component="span"
                            onClick={() => {
                                if (!effectiveReadOnly) onChange(id, { ...tdValue, toggled: !isToggled, selected: isToggled ? [] : selectedPills, details: isToggled ? '' : detailsText });
                            }}
                            sx={{
                                position: 'relative', display: 'inline-flex', flexShrink: 0,
                                width: 44, height: 26, borderRadius: '13px',
                                bgcolor: isToggled ? primaryColor : '#DFE3E8',
                                transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                                cursor: effectiveReadOnly ? 'default' : 'pointer',
                                p: '3px', boxSizing: 'border-box',
                            }}
                        >
                            <Box sx={{
                                width: 20, height: 20, borderRadius: '50%', bgcolor: '#fff',
                                boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
                                transform: isToggled ? 'translateX(18px)' : 'translateX(0px)',
                                transition: 'transform 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                            }} />
                        </Box>
                    </Box>

                    {/* Expanded content — smooth max-height CSS animation (300ms) */}
                    <Box sx={{
                        maxHeight: isToggled ? '500px' : 0,
                        overflow: 'hidden',
                        transition: 'max-height 0.3s ease-in-out',
                    }}>
                        <Box sx={{ px: 2, pb: 2, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                            {pillOptions.length > 0 && (
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                                    {pillOptions.map((opt) => {
                                        const optVal = typeof opt === 'string' ? opt : opt.value;
                                        const optLabel = typeof opt === 'string' ? opt : (opt.label || opt.value);
                                        const isSelected = selectedPills.includes(optVal);
                                        return (
                                            <Box
                                                key={optVal}
                                                onClick={() => {
                                                    if (effectiveReadOnly) return;
                                                    const newSelected = isSelected
                                                        ? selectedPills.filter((s) => s !== optVal)
                                                        : [...selectedPills, optVal];
                                                    onChange(id, { ...tdValue, selected: newSelected });
                                                }}
                                                sx={{
                                                    px: '14px',
                                                    py: '8px',
                                                    minHeight: '48px',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    borderRadius: '16px',
                                                    border: `1px solid ${isSelected ? primaryColor : '#DFE3E8'}`,
                                                    bgcolor: isSelected ? primaryColor : '#fff',
                                                    color: isSelected ? '#fff' : '#637381',
                                                    fontSize: '12px',
                                                    fontFamily: theme?.fontFamily || 'inherit',
                                                    cursor: effectiveReadOnly ? 'default' : 'pointer',
                                                    transition: 'all 0.15s',
                                                    userSelect: 'none',
                                                    transform: isSelected ? 'scale(1.03)' : 'scale(1)',
                                                }}
                                            >
                                                {optLabel}
                                            </Box>
                                        );
                                    })}
                                </Box>
                            )}
                            {showDetails && (
                                <textarea
                                    placeholder={detailsPlaceholder}
                                    value={detailsText}
                                    readOnly={effectiveReadOnly}
                                    onChange={(e) => onChange(id, { ...tdValue, details: e.target.value })}
                                    style={{
                                        width: '100%', minHeight: 80, padding: '8px 12px',
                                        borderRadius: 8, border: '1px solid #DFE3E8',
                                        fontFamily: theme?.fontFamily || 'inherit',
                                        fontSize: 13, color: '#212B36', resize: 'vertical',
                                        outline: 'none', boxSizing: 'border-box',
                                        backgroundColor: effectiveReadOnly ? '#f5f5f5' : '#fff',
                                    }}
                                />
                            )}
                        </Box>
                    </Box>

                    {/* Prefer not to say */}
                    {allowDecline && !isToggled && !effectiveReadOnly && (
                        <Box sx={{ px: 2, pb: 1.5 }}>
                            <Typography
                                variant="caption"
                                sx={{ color: '#919EAB', cursor: 'pointer', '&:hover': { color: '#637381' } }}
                                onClick={() => onChange(id, { toggled: 'declined', selected: [], details: '' })}
                            >
                                Prefer not to say
                            </Typography>
                        </Box>
                    )}
                </Box>
            );
        }

        case CONSENT_FIELD_TYPES.TOGGLE_TEXT: {
            const ttValue = value || {};
            const ttToggled = Boolean(ttValue.toggled);
            const ttText = ttValue.text || '';
            const ttPlaceholder = metadata?.placeholder || placeholder || 'Enter details...';

            return (
                <Box data-field-id={id} sx={{
                    border: `1px solid ${error ? '#FF4842' : ttToggled ? primaryColor : '#F2F3F5'}`,
                    borderRadius: '12px',
                    bgcolor: ttToggled ? '#F0FFF4' : '#fff',
                    transition: 'all 0.2s',
                    overflow: 'hidden',
                }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 2 }}>
                        <Typography variant="body2" sx={{
                            flex: 1, fontSize: '13px', fontWeight: 500,
                            color: fieldStyle.textColor || theme?.bodyColor || '#212B36',
                            fontFamily: theme?.fontFamily || 'inherit',
                            userSelect: 'none',
                        }}>
                            {label}{requiredIndicator}
                        </Typography>
                        <Box
                            component="span"
                            onClick={() => {
                                if (!effectiveReadOnly) onChange(id, { toggled: !ttToggled, text: ttToggled ? '' : ttText });
                            }}
                            sx={{
                                position: 'relative', display: 'inline-flex', flexShrink: 0,
                                width: 44, height: 26, borderRadius: '13px',
                                bgcolor: ttToggled ? primaryColor : '#DFE3E8',
                                transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                                cursor: effectiveReadOnly ? 'default' : 'pointer',
                                p: '3px', boxSizing: 'border-box',
                            }}
                        >
                            <Box sx={{
                                width: 20, height: 20, borderRadius: '50%', bgcolor: '#fff',
                                boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
                                transform: ttToggled ? 'translateX(18px)' : 'translateX(0px)',
                                transition: 'transform 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                            }} />
                        </Box>
                    </Box>
                    {ttToggled && (
                        <Box sx={{ px: 2, pb: 2 }}>
                            <textarea
                                placeholder={ttPlaceholder}
                                value={ttText}
                                readOnly={effectiveReadOnly}
                                onChange={(e) => onChange(id, { toggled: true, text: e.target.value })}
                                style={{
                                    width: '100%', minHeight: 80, padding: '8px 12px',
                                    borderRadius: 8, border: '1px solid #DFE3E8',
                                    fontFamily: theme?.fontFamily || 'inherit',
                                    fontSize: 13, color: '#212B36', resize: 'vertical',
                                    outline: 'none', boxSizing: 'border-box',
                                    backgroundColor: effectiveReadOnly ? '#f5f5f5' : '#fff',
                                }}
                            />
                        </Box>
                    )}
                </Box>
            );
        }

        case CONSENT_FIELD_TYPES.PILL_SELECT: {
            const psOptions = options || [];
            return (
                <Box data-field-id={id}>
                    {renderLabel()}
                    <Box sx={{
                        display: 'flex', flexWrap: 'wrap', gap: 1, mt: 0.5,
                        '@media (max-width: 480px)': {
                            display: 'grid',
                            gridTemplateColumns: 'repeat(2, 1fr)',
                        },
                    }}>
                        {psOptions.map((opt) => {
                            const optVal = typeof opt === 'string' ? opt : opt.value;
                            const optLabel = typeof opt === 'string' ? opt : (opt.label || opt.value);
                            const isSelected = value === optVal;
                            return (
                                <Box
                                    key={optVal}
                                    onClick={() => {
                                        if (!effectiveReadOnly) onChange(id, isSelected ? '' : optVal);
                                    }}
                                    sx={{
                                        px: '18px', py: '10px', minHeight: '48px',
                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                        borderRadius: '20px',
                                        border: `1.5px solid ${error ? '#FF4842' : isSelected ? primaryColor : '#DFE3E8'}`,
                                        bgcolor: isSelected ? primaryColor : '#fff',
                                        color: isSelected ? '#fff' : '#637381',
                                        fontSize: '13px', fontFamily: theme?.fontFamily || 'inherit',
                                        fontWeight: isSelected ? 600 : 400,
                                        cursor: effectiveReadOnly ? 'default' : 'pointer',
                                        transition: 'all 0.15s ease',
                                        userSelect: 'none',
                                        boxShadow: isSelected ? `0 2px 8px ${primaryLight}` : 'none',
                                        '&:hover': effectiveReadOnly ? {} : {
                                            bgcolor: isSelected ? primaryColor : `${primaryColor}08`,
                                            borderColor: primaryColor,
                                        },
                                    }}
                                >
                                    {isSelected && (
                                        <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" style={{ marginRight: 6, flexShrink: 0 }}>
                                            <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z" />
                                        </svg>
                                    )}
                                    {optLabel}
                                </Box>
                            );
                        })}
                    </Box>
                    {renderError(error)}
                </Box>
            );
        }

        case CONSENT_FIELD_TYPES.LIKERT_SCALE: {
            const defaultLikertOptions = [
                { label: 'Excellent', value: '5' },
                { label: 'Good', value: '4' },
                { label: 'Fair', value: '3' },
                { label: 'Poor', value: '2' },
                { label: 'Very Poor', value: '1' },
            ];
            const lsOptions = (options && options.length > 0) ? options : defaultLikertOptions;
            return (
                <Box data-field-id={id}>
                    {renderLabel()}
                    <Box sx={{
                        display: 'flex', flexWrap: 'wrap', gap: 0.75, mt: 0.5,
                        '@media (max-width: 480px)': {
                            display: 'grid',
                            gridTemplateColumns: 'repeat(2, 1fr)',
                        },
                    }}>
                        {lsOptions.map((opt) => {
                            const optVal = typeof opt === 'string' ? opt : opt.value;
                            const optLabel = typeof opt === 'string' ? opt : (opt.label || opt.value);
                            const isSelected = String(value) === String(optVal);
                            return (
                                <Box
                                    key={optVal}
                                    onClick={() => {
                                        if (!effectiveReadOnly) onChange(id, optVal);
                                    }}
                                    sx={{
                                        px: '18px', py: '10px', minHeight: '48px',
                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                        borderRadius: '20px',
                                        border: `1.5px solid ${error ? '#FF4842' : isSelected ? primaryColor : '#DFE3E8'}`,
                                        bgcolor: isSelected ? primaryColor : '#fff',
                                        color: isSelected ? '#fff' : '#637381',
                                        fontSize: '13px', fontFamily: theme?.fontFamily || 'inherit',
                                        fontWeight: isSelected ? 600 : 400,
                                        cursor: effectiveReadOnly ? 'default' : 'pointer',
                                        transition: 'all 0.15s ease',
                                        userSelect: 'none',
                                        boxShadow: isSelected ? `0 2px 8px ${primaryLight}` : 'none',
                                        '&:hover': effectiveReadOnly ? {} : {
                                            bgcolor: isSelected ? primaryColor : `${primaryColor}08`,
                                            borderColor: primaryColor,
                                        },
                                    }}
                                >
                                    {optLabel}
                                </Box>
                            );
                        })}
                    </Box>
                    {renderError(error)}
                </Box>
            );
        }

        case CONSENT_FIELD_TYPES.NPS_SCALE: {
            // Colour gradient: 0-6 = red/warm, 7-8 = amber/yellow, 9-10 = green
            const NPS_COLORS = [
                '#D32F2F', // 0
                '#E53935', // 1
                '#F44336', // 2
                '#FF5722', // 3
                '#FF7043', // 4
                '#FFA726', // 5
                '#FFCA28', // 6
                '#FFD54F', // 7
                '#FFF176', // 8 — lighter yellow
                '#AED581', // 9
                '#66BB6A', // 10
            ];
            const selectedNPS = value !== undefined && value !== null && value !== '' ? String(value) : null;
            const labelLow = metadata?.label_low || 'Not at all likely';
            const labelHigh = metadata?.label_high || 'Extremely likely';

            return (
                <Box data-field-id={id}>
                    {renderLabel()}
                    {/* Number row — wraps to two rows on narrow screens */}
                    <Box sx={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: '6px',
                        mt: 1,
                        // Two rows of 6+5 on very narrow screens
                        '@media (max-width: 480px)': {
                            display: 'grid',
                            gridTemplateColumns: 'repeat(6, 1fr)',
                        },
                    }}>
                        {NPS_COLORS.map((color, score) => {
                            const scoreStr = String(score);
                            const isSelected = selectedNPS === scoreStr;
                            return (
                                <Box
                                    key={score}
                                    role="radio"
                                    aria-checked={isSelected}
                                    aria-label={`Score ${score}`}
                                    tabIndex={0}
                                    onClick={() => { if (!effectiveReadOnly) onChange(id, isSelected ? '' : scoreStr); }}
                                    onKeyDown={(e) => {
                                        if (!effectiveReadOnly && (e.key === 'Enter' || e.key === ' ')) {
                                            e.preventDefault();
                                            onChange(id, isSelected ? '' : scoreStr);
                                        }
                                    }}
                                    sx={{
                                        minWidth: '44px',
                                        minHeight: '44px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        borderRadius: '8px',
                                        border: `2px solid ${error ? '#FF4842' : isSelected ? color : 'rgba(145,158,171,0.2)'}`,
                                        bgcolor: isSelected ? color : '#fff',
                                        color: isSelected ? '#fff' : '#637381',
                                        fontSize: '15px',
                                        fontWeight: isSelected ? 700 : 500,
                                        fontFamily: theme?.fontFamily || 'inherit',
                                        cursor: effectiveReadOnly ? 'default' : 'pointer',
                                        transition: 'all 0.15s ease',
                                        userSelect: 'none',
                                        flex: '0 0 auto',
                                        boxShadow: isSelected ? `0 2px 8px ${color}55` : 'none',
                                        transform: isSelected ? 'scale(1.1)' : 'scale(1)',
                                        '&:hover': effectiveReadOnly ? {} : {
                                            bgcolor: isSelected ? color : `${color}22`,
                                            borderColor: color,
                                            color: isSelected ? '#fff' : color,
                                        },
                                    }}
                                >
                                    {score}
                                </Box>
                            );
                        })}
                    </Box>
                    {/* Scale labels */}
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
                        <Typography variant="caption" sx={{ color: 'text.disabled', fontSize: '11px', fontFamily: theme?.fontFamily || 'inherit' }}>
                            {labelLow}
                        </Typography>
                        <Typography variant="caption" sx={{ color: 'text.disabled', fontSize: '11px', fontFamily: theme?.fontFamily || 'inherit' }}>
                            {labelHigh}
                        </Typography>
                    </Box>
                    {renderError(error)}
                </Box>
            );
        }

        default:
            return (
                <Box>
                    <Typography variant="body2" color="text.secondary">{label} ({type})</Typography>
                </Box>
            );
    }
};

ConsentFieldRenderer.propTypes = {
    field: PropTypes.object.isRequired,
    value: PropTypes.any,
    onChange: PropTypes.func.isRequired,
    error: PropTypes.string,
    readOnly: PropTypes.bool,
    theme: PropTypes.object,
    onBlur: PropTypes.func,
    // When true, the field renders in its "fully expanded" presentation
    // (no Learn more / Show less collapsibles) so html2canvas captures
    // the complete helper text. Used by the off-screen PDF snapshot in
    // AdvancedFormFlow.
    forPdf: PropTypes.bool,
};

ConsentFieldRenderer.defaultProps = {
    value: undefined,
    error: '',
    readOnly: false,
    theme: null,
    onBlur: null,
    forPdf: false,
};

export default ConsentFieldRenderer;
